import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { locationData } from '../data/mock';
import { motion } from 'framer-motion';
import { Clock, Phone, MessageCircle, Navigation } from 'lucide-react';

const LocationCard = ({ location, index, t, language }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay: index * 0.12 }}
    className="group bg-white rounded-3xl overflow-hidden shadow-sm border border-[#E2E8F0] hover:shadow-xl hover:border-[#0D9488]/20 transition-all duration-500 flex flex-col"
  >
    {/* Image Header */}
    <div className="relative h-48 overflow-hidden">
      <img
        src={location.image}
        alt={`${location.name} - Best mariscos restaurant in ${location.name === 'El Tezal' ? 'Cabo San Lucas' : 'Los Cabos'} - Fresh Sinaloa seafood`}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0C4A6E]/70 via-[#0C4A6E]/20 to-transparent" />
      <div className="absolute bottom-4 left-5 right-5">
        <h3 className="font-display text-xl font-bold text-white leading-tight">{location.name}</h3>
      </div>
    </div>

    {/* Content */}
    <div className="p-6 flex flex-col flex-1">
      {/* Description */}
      <p className="text-[#475569] text-sm leading-relaxed mb-5 flex-1">
        {location.description[language]}
      </p>

      {/* Hours */}
      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-[#E2E8F0]">
        <div className="w-9 h-9 rounded-xl bg-[#0D9488]/8 flex items-center justify-center flex-shrink-0">
          <Clock className="w-4 h-4 text-[#0D9488]" />
        </div>
        <div>
          <p className="text-xs font-semibold text-[#334155] uppercase tracking-wider">{t('locations.hours')}</p>
          <p className="text-sm text-[#475569]">{location.hours ? location.hours[language] : t('locations.hoursValue')}</p>
        </div>
      </div>

      {/* Phone */}
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-xl bg-[#0D9488]/8 flex items-center justify-center flex-shrink-0">
          <Phone className="w-4 h-4 text-[#0D9488]" />
        </div>
        <a href={`tel:${location.phoneClean}`} className="text-sm font-medium text-[#334155] hover:text-[#0D9488] transition-colors">
          {location.phone}
        </a>
      </div>

      {/* Action Buttons */}
      <div className="space-y-2.5">
        {/* Call Now */}
        <a
          href={`tel:${location.phoneClean}`}
          className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl bg-[#0C4A6E] text-white text-sm font-semibold hover:bg-[#0C4A6E]/90 transition-colors"
        >
          <Phone className="w-4 h-4" />
          {t('locations.call')}
        </a>

        {/* Reserve via WhatsApp */}
        <a
          href={`${location.whatsappLink}?text=${encodeURIComponent(
            location.whatsappText
              ? (language === 'en' ? `Hello! I'd like to place an order at the ${location.name} location.` : `¡Hola! Me gustaría hacer un pedido en la sucursal ${location.name}.`)
              : (language === 'en' ? `Hello! I'd like to make a reservation at the ${location.name} location.` : `¡Hola! Me gustaría hacer una reservación en la sucursal ${location.name}.`)
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl bg-[#0D9488] text-white text-sm font-semibold hover:bg-[#0F766E] transition-colors"
        >
          <MessageCircle className="w-4 h-4" />
          {location.whatsappText ? location.whatsappText[language] : t('locations.whatsapp')}
        </a>

        {/* Get Directions */}
        <a
          href={location.mapsLink}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl border-2 border-[#E2E8F0] text-[#334155] text-sm font-semibold hover:border-[#0D9488] hover:text-[#0D9488] transition-colors"
        >
          <Navigation className="w-4 h-4" />
          {t('locations.getDirections')}
        </a>
      </div>
    </div>
  </motion.div>
);

export const Locations = () => {
  const { language, t } = useLanguage();

  return (
    <section id="locations" className="relative py-28 lg:py-36 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-2xl mx-auto text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-sm font-semibold text-[#0D9488] tracking-[0.2em] uppercase mb-4">
              {t('locations.tagline')}
            </span>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#0C4A6E] mb-4">
              {t('locations.title')}
            </h2>
            <p className="text-lg text-[#64748B] mb-2">
              {t('locations.subtitle')}
            </p>
            <p className="text-base text-[#94A3B8]">
              {t('locations.seoSubtitle')}
            </p>
          </motion.div>
        </div>

        {/* SEO Heading for Locations */}
        <h3 className="sr-only">Seafood Restaurant Locations in Cabo San Lucas and Los Cabos</h3>

        {/* Location Cards - 3 column grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {locationData.map((location, index) => (
            <LocationCard
              key={location.id}
              location={location}
              index={index}
              t={t}
              language={language}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
